import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const PRODUCTS_COLLECTION = 'products';

// In-memory and session response cache to make page transitions instant and reduce Firestore reads
const productsCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min TTL
const SESSION_CACHE_PREFIX = 'rangaara_prod_cache_';

export const clearProductCache = () => {
  productsCache.clear();
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith(SESSION_CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    }
  } catch (e) {
    // Ignore sessionStorage access error in restricted contexts
  }
};

/**
 * Get list of products with optional filtering
 */
export const getProducts = async (filters = {}) => {
  try {
    const cacheKey = JSON.stringify(filters);

    // 1. Check in-memory cache
    const memoryCached = productsCache.get(cacheKey);
    if (memoryCached && (Date.now() - memoryCached.timestamp < CACHE_TTL_MS)) {
      return memoryCached.data;
    }

    // 2. Check sessionStorage cache
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const sessionRaw = sessionStorage.getItem(SESSION_CACHE_PREFIX + cacheKey);
        if (sessionRaw) {
          const sessionParsed = JSON.parse(sessionRaw);
          if (sessionParsed && (Date.now() - sessionParsed.timestamp < CACHE_TTL_MS)) {
            productsCache.set(cacheKey, sessionParsed);
            return sessionParsed.data;
          }
        }
      }
    } catch (e) {
      // Ignore sessionStorage parsing error
    }

    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const constraints = [];

    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.featured) {
      constraints.push(where('featured', '==', true));
    }
    if (filters.bestseller) {
      constraints.push(where('bestseller', '==', true));
    }
    if (filters.newArrival) {
      constraints.push(where('newArrival', '==', true));
    }
    if (filters.limit && typeof filters.limit === 'number') {
      constraints.push(limit(filters.limit));
    }

    const q = constraints.length > 0 ? query(productsRef, ...constraints) : query(productsRef);

    const snapshot = await getDocs(q);
    let products = snapshot.docs.map(docSnap => ({
      _id: docSnap.id,
      id: docSnap.id,
      ...docSnap.data()
    }));

    // In-memory search filter if search parameter passed
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      products = products.filter(p => 
        (p.name && p.name.toLowerCase().includes(searchLower)) ||
        (p.description && p.description.toLowerCase().includes(searchLower))
      );
    }

    const result = {
      products,
      total: products.length,
      totalPages: 1,
      currentPage: 1
    };

    const cacheEntry = { timestamp: Date.now(), data: result };
    productsCache.set(cacheKey, cacheEntry);
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem(SESSION_CACHE_PREFIX + cacheKey, JSON.stringify(cacheEntry));
      }
    } catch (e) {
      // Quota exceeded or private browsing
    }

    return result;
  } catch (error) {
    console.error('Error fetching products from Firestore:', error);
    throw error;
  }
};

/**
 * Get product by ID
 */
export const getProductById = async (id) => {
  try {
    const docRef = doc(db, PRODUCTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return {
      _id: docSnap.id,
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Error fetching product by ID from Firestore:', error);
    throw error;
  }
};

/**
 * Create a new product in Firestore
 */
export const createProduct = async (productData) => {
  try {
    clearProductCache();
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const docRef = await addDoc(productsRef, {
      ...productData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    const newDoc = await getDoc(docRef);
    return {
      _id: docRef.id,
      id: docRef.id,
      ...newDoc.data()
    };
  } catch (error) {
    console.error('Error creating product in Firestore:', error);
    throw error;
  }
};

/**
 * Update an existing product in Firestore
 */
export const updateProduct = async (id, updateData) => {
  try {
    clearProductCache();
    const docRef = doc(db, PRODUCTS_COLLECTION, id);
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });

    const updatedDoc = await getDoc(docRef);
    return {
      _id: id,
      id: id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error('Error updating product in Firestore:', error);
    throw error;
  }
};

/**
 * Delete a product from Firestore
 */
export const deleteProduct = async (id) => {
  try {
    clearProductCache();
    const docRef = doc(db, PRODUCTS_COLLECTION, id);
    await deleteDoc(docRef);
    return { success: true, message: 'Product deleted successfully' };
  } catch (error) {
    console.error('Error deleting product from Firestore:', error);
    throw error;
  }
};
